<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <span class="headline font-weight-bold">Caddie House - Resguardo de Equipos</span>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="openTournamentDialog" class="mr-2">
                        <v-icon left>mdi-file-upload</v-icon>
                        Librillo Torneo
                    </v-btn>
                </v-card-title>

                <!-- Tabs -->
                <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
                    <v-tab>
                        <v-icon left>mdi-package-variant</v-icon>
                        Entrega/Recepción
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-history</v-icon>
                        Historial
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="activeTab" class="transparent">
                    <!-- Tab 1: Entrega/Recepción -->
                    <v-tab-item>
                        <!-- Estadísticas -->
                        <v-row class="mb-4">
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold primary--text">{{ bagsToday }}</div>
                                    <div class="caption grey--text">Totales</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold green--text">{{ deliveredBags }}</div>
                                    <div class="caption grey--text">Entregadas</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold orange--text">{{ receivedBags }}</div>
                                    <div class="caption grey--text">Recibidas</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold info--text">{{ pendingBags }}</div>
                                    <div class="caption grey--text">Pendientes</div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Tabla de Bolsas -->
                        <v-card class="elevation-1 mb-4">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-briefcase</v-icon>
                                Bolsas en Resguardo
                            </v-card-subtitle>
                            <v-data-table
                                :headers="bagHeaders"
                                :items="bags"
                                class="elevation-0"
                                :no-data-text="'Sin bolsas registradas'">

                                <template v-slot:item.member="{ item }">
                                    <div class="font-weight-bold">{{ item.member_name }}</div>
                                    <div class="caption grey--text">Caddie: {{ item.caddie_name }}</div>
                                </template>

                                <template v-slot:item.status="{ item }">
                                    <v-chip small :color="getStatusColor(item.status)" text-color="white">
                                        {{ getStatusLabel(item.status) }}
                                    </v-chip>
                                </template>

                                <template v-slot:item.time="{ item }">
                                    <div class="font-weight-bold">{{ item.time }}</div>
                                    <div class="caption grey--text">{{ item.date }}</div>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                icon
                                                small
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="openChecklist(item)"
                                                :disabled="item.status === 'completado'">
                                                <v-icon small>mdi-clipboard-list</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Hacer inventario</span>
                                    </v-tooltip>
                                    <v-tooltip bottom v-if="item.status !== 'completado'">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="viewChecklist(item)">
                                                <v-icon small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver inventario</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>

                    <!-- Tab 2: Historial -->
                    <v-tab-item>
                        <v-card class="elevation-1">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-history</v-icon>
                                Historial de Inventarios
                            </v-card-subtitle>
                            <v-data-table
                                :headers="historyHeaders"
                                :items="inventoryHistory"
                                class="elevation-0"
                                :items-per-page="10"
                                :no-data-text="'Sin historial'"
                                sort-by="date"
                                :sort-desc="true">

                                <template v-slot:item.date="{ item }">
                                    <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                                    <div class="caption grey--text">{{ item.time }}</div>
                                </template>

                                <template v-slot:item.member="{ item }">
                                    <div class="font-weight-bold">{{ item.member_name }}</div>
                                    <div class="caption grey--text">{{ item.caddie_name }}</div>
                                </template>

                                <template v-slot:item.action="{ item }">
                                    <v-chip small :color="item.action === 'entrega' ? 'primary' : 'success'" text-color="white">
                                        {{ item.action === 'entrega' ? 'Entrega' : 'Recepción' }}
                                    </v-chip>
                                </template>

                                <template v-slot:item.details="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon x-small v-bind="attrs" v-on="on" @click="viewDetails(item)">
                                                <v-icon x-small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver inventario</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>
                </v-tabs-items>
            </v-flex>
        </v-layout>

        <!-- Diálogo: Checklist de Inventario -->
        <v-dialog v-model="checklistDialog" max-width="800px" persistent>
            <v-card v-if="currentBag">
                <v-card-title>
                    <div>
                        <div class="headline">Checklist de Inventario</div>
                        <div class="caption grey--text">{{ currentBag.member_name }} - Caddie: {{ currentBag.caddie_name }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="closeChecklist" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="checklistForm" v-model="validChecklist" lazy-validation>
                        <!-- REQ-28: Conteo de Bastones y Accesorios -->
                        <div class="subtitle-2 font-weight-bold mb-4 mt-4">Número de Bastones: {{ currentChecklist.clubs_irons + currentChecklist.clubs_driver + currentChecklist.clubs_putter + currentChecklist.clubs_hybrid }}</div>
                        <div class="subtitle-2 font-weight-bold mb-4">Bastones</div>
                        <v-row>
                            <v-col cols="12" sm="3">
                                <v-text-field
                                    v-model.number="currentChecklist.clubs_irons"
                                    label="Fierros"
                                    type="number"
                                    outlined
                                    dense
                                    min="0"
                                    max="14"
                                    hint="Cantidad de fierros"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-text-field
                                    v-model.number="currentChecklist.clubs_driver"
                                    label="Driver"
                                    type="number"
                                    outlined
                                    dense
                                    min="0"
                                    max="5"
                                    hint="Cantidad de drivers"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-text-field
                                    v-model.number="currentChecklist.clubs_putter"
                                    label="Putter"
                                    type="number"
                                    outlined
                                    dense
                                    min="0"
                                    max="3"
                                    hint="Cantidad de putters"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-text-field
                                    v-model.number="currentChecklist.clubs_hybrid"
                                    label="Híbridos"
                                    type="number"
                                    outlined
                                    dense
                                    min="0"
                                    max="3"
                                    hint="Cantidad de híbridos"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4"></v-divider>

                        <!-- REQ-30: Accesorios y Switches -->
                        <div class="subtitle-2 font-weight-bold mb-4">Accesorios</div>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-switch
                                    v-model="currentChecklist.has_umbrella"
                                    label="¿Paraguas en la bolsa?"
                                    color="primary"
                                ></v-switch>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-switch
                                    v-model="currentChecklist.has_pullcar"
                                    label="¿Pullcar en la bolsa?"
                                    color="primary"
                                ></v-switch>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-switch
                                    v-model="currentChecklist.has_towel"
                                    label="¿Toalla en la bolsa?"
                                    color="primary"
                                ></v-switch>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="currentChecklist.high_value_items"
                                    label="Registre accesorios especiales (telémetros, calzado especial, GPS, etc.)"
                                    outlined
                                    dense
                                    rows="4"
                                    placeholder="Ej: Telémetro Nikon, Calzado FootJoy Nike, etc."
                                    counter
                                    maxlength="500"
                                ></v-textarea>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4"></v-divider>

                        <!-- REQ-29: Fundas de Protección -->
                        <div class="subtitle-2 font-weight-bold mb-4">Fundas de Protección</div>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model.number="currentChecklist.covers_woods"
                                    label="Fundas - Maderas"
                                    type="number"
                                    outlined
                                    dense
                                    min="0"
                                    hint="Fundas de protección de cabezas"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model.number="currentChecklist.covers_putters"
                                    label="Fundas - Putters"
                                    type="number"
                                    outlined
                                    dense
                                    min="0"
                                    hint="Fundas de protección de putters"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Notas Adicionales -->
                        <v-row class="mt-4">
                            <v-col cols="12">
                                <v-textarea
                                    v-model="currentChecklist.notes"
                                    label="Observaciones/Notas"
                                    outlined
                                    dense
                                    rows="3"
                                    placeholder="Cualquier observación sobre el estado de los equipos"
                                    counter
                                    maxlength="300"
                                ></v-textarea>
                            </v-col>
                        </v-row>

                        <!-- Resumen -->
                        <v-row class="mt-4">
                            <v-col cols="12">
                                <v-alert type="info" outlined>
                                    <div class="subtitle-2 font-weight-bold mb-2">Resumen del Inventario</div>
                                    <div class="caption">
                                        <div>Bastones: {{ currentChecklist.clubs_irons + currentChecklist.clubs_driver + currentChecklist.clubs_putter + currentChecklist.clubs_hybrid }} total</div>
                                        <div>Fundas: {{ currentChecklist.covers_woods + currentChecklist.covers_putters }} total</div>
                                        <div>Paraguas: {{ currentChecklist.has_umbrella ? 'Sí' : 'No' }} | Pullcar: {{ currentChecklist.has_pullcar ? 'Sí' : 'No' }} | Toalla: {{ currentChecklist.has_towel ? 'Sí' : 'No' }}</div>
                                        <div v-if="currentChecklist.high_value_items">Accesorios especiales: {{ currentChecklist.high_value_items.split(',').length }} registrados</div>
                                    </div>
                                </v-alert>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeChecklist">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveChecklist" :disabled="!validChecklist">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar Inventario
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Ver Inventario -->
        <v-dialog v-model="viewDialog" max-width="600px">
            <v-card v-if="viewingRecord">
                <v-card-title>
                    <div>
                        <div class="headline">Inventario - {{ viewingRecord.member_name }}</div>
                        <div class="caption grey--text">{{ formatDate(viewingRecord.date) }} - {{ viewingRecord.time }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="viewDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-list dense>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Fierros</div>
                                <div class="body-2 font-weight-bold">{{ viewingRecord.clubs_irons }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Driver</div>
                                <div class="body-2 font-weight-bold">{{ viewingRecord.clubs_driver }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Putter</div>
                                <div class="body-2 font-weight-bold">{{ viewingRecord.clubs_putter }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Híbridos</div>
                                <div class="body-2 font-weight-bold">{{ viewingRecord.clubs_hybrid }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-2"></v-divider>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Fundas - Maderas</div>
                                <div class="body-2 font-weight-bold">{{ viewingRecord.covers_woods }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Fundas - Putters</div>
                                <div class="body-2 font-weight-bold">{{ viewingRecord.covers_putters }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-2"></v-divider>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Paraguas</div>
                                <v-chip small :color="viewingRecord.has_umbrella ? 'green' : 'grey'" text-color="white">
                                    {{ viewingRecord.has_umbrella ? 'Sí' : 'No' }}
                                </v-chip>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Pullcar</div>
                                <v-chip small :color="viewingRecord.has_pullcar ? 'green' : 'grey'" text-color="white">
                                    {{ viewingRecord.has_pullcar ? 'Sí' : 'No' }}
                                </v-chip>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Toalla</div>
                                <v-chip small :color="viewingRecord.has_towel ? 'green' : 'grey'" text-color="white">
                                    {{ viewingRecord.has_towel ? 'Sí' : 'No' }}
                                </v-chip>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-2"></v-divider>
                        <v-list-item v-if="viewingRecord.high_value_items">
                            <v-list-item-content>
                                <div class="caption grey--text">Accesorios</div>
                                <div class="body-2">{{ viewingRecord.high_value_items }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="viewingRecord.notes">
                            <v-list-item-content>
                                <div class="caption grey--text">Observaciones</div>
                                <div class="body-2">{{ viewingRecord.notes }}</div>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Cargar Librillo del Torneo -->
        <v-dialog v-model="tournamentDialog" max-width="600px">
            <v-card>
                <v-card-title>
                    <div>
                        <div class="headline">Cargar Librillo - Torneo Anual</div>
                        <div class="caption grey--text">Cargue el archivo del torneo anual de golf</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="tournamentDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="tournamentForm" v-model="validTournament" lazy-validation>
                        <v-file-input
                            v-model="tournamentFile"
                            label="Seleccione archivo (PDF, Excel, etc.)"
                            outlined
                            dense
                            show-size
                            accept=".pdf,.xls,.xlsx,.doc,.docx"
                            :rules="[v => v || 'Se requiere un archivo']"
                        ></v-file-input>
                        <v-text-field
                            v-model="tournamentDate"
                            label="Fecha del Torneo"
                            type="date"
                            outlined
                            dense
                            :rules="[v => v || 'Se requiere una fecha']"
                            class="mt-4"
                        ></v-text-field>
                        <v-textarea
                            v-model="tournamentNotes"
                            label="Observaciones (opcional)"
                            outlined
                            dense
                            rows="3"
                            placeholder="Notas adicionales sobre el torneo"
                            counter
                            maxlength="300"
                            class="mt-4"
                        ></v-textarea>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="tournamentDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveTournamentFile" :disabled="!validTournament">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </v-container>
</template>

<script>
import { caddieHouseAPI, caddieHouseUtils } from '@/services/caddieHouse'

export default {
  name: 'CaddieHouse',
  data: () => ({
    activeTab: 0,
    checklistDialog: false,
    viewDialog: false,
    tournamentDialog: false,
    validChecklist: false,
    validTournament: false,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    currentBag: null,
    viewingRecord: null,
    tournamentFile: null,
    tournamentDate: '',
    tournamentNotes: '',
    loading: false,
    bagHeaders: [
      { text: 'Socio / Caddie', value: 'member', width: '35%' },
      { text: 'Estado', value: 'status', width: '15%' },
      { text: 'Hora / Fecha', value: 'time', width: '25%' },
      { text: 'Acciones', value: 'actions', width: '15%', sortable: false, align: 'center' }
    ],
    historyHeaders: [
      { text: 'Fecha / Hora', value: 'date', width: '20%' },
      { text: 'Socio / Caddie', value: 'member', width: '35%' },
      { text: 'Acción', value: 'action', width: '15%' },
      { text: 'Bastones', value: 'total_clubs', width: '15%', align: 'center' },
      { text: 'Detalles', value: 'details', width: '10%', sortable: false, align: 'center' }
    ],
    currentChecklist: {
      clubs_irons: 0,
      clubs_driver: 0,
      clubs_putter: 0,
      clubs_hybrid: 0,
      covers_woods: 0,
      covers_putters: 0,
      has_umbrella: false,
      has_pullcar: false,
      has_towel: false,
      high_value_items: '',
      notes: ''
    },
    defaultChecklist: {
      clubs_irons: 0,
      clubs_driver: 0,
      clubs_putter: 0,
      clubs_hybrid: 0,
      covers_woods: 0,
      covers_putters: 0,
      has_umbrella: false,
      has_pullcar: false,
      has_towel: false,
      high_value_items: '',
      notes: ''
    },
    bags: [],
    inventoryHistory: []
  }),
  computed: {
    bagsToday () {
      return this.bags.length
    },
    deliveredBags () {
      return this.bags.filter(b => b.status === 'entrega').length
    },
    receivedBags () {
      return this.bags.filter(b => b.status === 'recepcion').length
    },
    pendingBags () {
      return this.bags.filter(b => b.status !== 'completado').length
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadBags(),
          this.loadInventoryHistory()
        ])
      } catch (error) {
        console.error('Error loading caddie house data:', error)
        this.showSnackbar('Error al cargar datos', 'error')
      } finally {
        this.loading = false
      }
    },
    async loadBags () {
      try {
        const response = await caddieHouseAPI.getBags()
        this.bags = response.results || []
      } catch (error) {
        console.error('Error loading bags:', error)
        this.showSnackbar('Error al cargar bolsas', 'error')
      }
    },
    async loadInventoryHistory () {
      try {
        const response = await caddieHouseAPI.getInventoryHistory({ limit: 50 })
        this.inventoryHistory = response.results || []
      } catch (error) {
        console.error('Error loading inventory history:', error)
        this.showSnackbar('Error al cargar histórico', 'error')
      }
    },
    showSnackbar (message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },
    getStatusColor (status) {
      const colors = {
        entrega: 'blue',
        recepcion: 'orange',
        completado: 'green'
      }
      return colors[status] || 'grey'
    },
    getStatusLabel (status) {
      const labels = {
        entrega: 'Entrega',
        recepcion: 'Recepción',
        completado: 'Completado'
      }
      return labels[status] || 'Desconocido'
    },
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    openChecklist (bag) {
      this.currentBag = { ...bag }
      this.currentChecklist = { ...this.defaultChecklist }
      this.checklistDialog = true
    },
    closeChecklist () {
      this.checklistDialog = false
      this.currentBag = null
      this.currentChecklist = { ...this.defaultChecklist }
    },
    async saveChecklist () {
      if (this.$refs.checklistForm.validate()) {
        try {
          const inventoryData = {
            bag_id: this.currentBag.id,
            ...this.currentChecklist
          }

          await caddieHouseAPI.createInventoryRecord(inventoryData)
          this.showSnackbar('Inventario guardado correctamente', 'success')
          this.closeChecklist()
          this.loadData()
        } catch (error) {
          console.error('Error saving checklist:', error)
          const message = error.response?.data?.message || 'Error al guardar el inventario'
          this.showSnackbar(message, 'error')
        }
      }
    },
    viewChecklist (bag) {
      const record = this.inventoryHistory.find(r => r.member_name === bag.member_name && r.caddie_name === bag.caddie_name)
      if (record) {
        this.viewingRecord = { ...record }
        this.viewDialog = true
      }
    },
    viewDetails (record) {
      this.viewingRecord = { ...record }
      this.viewDialog = true
    },
    openTournamentDialog () {
      this.tournamentFile = null
      this.tournamentDate = ''
      this.tournamentNotes = ''
      this.tournamentDialog = true
    },
    async saveTournamentFile () {
      if (this.$refs.tournamentForm.validate()) {
        try {
          caddieHouseUtils.isValidFile(this.tournamentFile)

          await caddieHouseAPI.uploadTournamentFile(
            this.tournamentFile,
            this.tournamentDate,
            this.tournamentNotes
          )

          this.showSnackbar(`Librillo cargado correctamente: ${this.tournamentFile.name}`, 'success')
          this.tournamentDialog = false
        } catch (error) {
          console.error('Error uploading tournament file:', error)
          const message = error.message || error.response?.data?.message || 'Error al cargar el archivo'
          this.showSnackbar(message, 'error')
        }
      }
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
